import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import classNames from "classnames";
import { PaymentMethod } from "./_components/PaymentMethod/PaymentMethod.tsx";
import { PackageSelection } from "./_components/PackageSelection/PackageSelection.tsx";
import { ApplicationSelection } from "./_components/ApplictaionSelection/ApplicationSelection.tsx";
import Logo from "@/assets/svg/logo.svg";
import People from "@/assets/svg/people.svg";
import { PACKAGE_MAPPINGS } from "@/constants/index.ts";
import {
  ApplicationOption,
  PackageMappings,
  Package,
  Section,
} from "./dashboard.types.ts";
import LoadingPreloader from "@/components/LoadingPreloader/LoadingPreloader.tsx";
import { toast, ToastContainer } from "react-toastify";

import "@/assets/styles/_dashboard.scss";
import usePackages from "@/hooks/usePackages.tsx";

const DopingHafizaApp: React.FC = () => {
  const { packages, user, uuid, loading } = usePackages();

  const [selectedOption, setSelectedOption] = useState<string>("");
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [sections, setSections] = useState<Section[]>([
    { id: "uygulama-seçimi", title: "Uygulama Seçimi", isExpanded: true },
    { id: "paket-seçimi", title: "Paket Seçimi", isExpanded: false },
    { id: "ödeme-yöntemi", title: "Ödeme Yöntemi", isExpanded: false },
  ]);

  const packageSelectionRef = useRef<HTMLDivElement>(null);
  const paymentMethodRef = useRef<HTMLDivElement>(null);
  const paymentMethodChildRef = useRef<any>(null);

  const applicationOptions: ApplicationOption[] = [
    {
      id: "COACH",
      title: "Koçum Yanımda",
      description:
        "Koçum Yanımda ile uzman psikologlarımızla rehberlik görüşmesi sağlayabilirsiniz.",
      icon: null,
      selected: false,
    },
    {
      id: "GOT_IT",
      title: "Şimdi Anladım",
      description: "Şimdi Anladım ile konuları daha iyi anlayabilirsiniz.",
      icon: null,
      selected: false,
    },
    {
      id: "SOLVER",
      title: "Çözücü",
      description: "Çözücü ile sorularınızı hızlıca çözebilirsiniz.",
      icon: null,
      selected: true,
    },
  ];

  const getFilteredPackages = (): Package[] => {
    if (!selectedOption || !packages || !Array.isArray(packages)) {
      return [];
    }

    const packageMappings = PACKAGE_MAPPINGS as PackageMappings;
    const allowedPackageIds = packageMappings[selectedOption] || [];

    if (allowedPackageIds.length === 0) {
      return [];
    }

    const filtered = packages.filter((pkg: any): pkg is Package => {
      return (
        pkg &&
        pkg.id &&
        typeof pkg.id === "string" &&
        allowedPackageIds.includes(pkg.id) &&
        pkg.description &&
        typeof pkg.description === "string" &&
        pkg.price &&
        typeof pkg.price === "string"
      );
    });

    return filtered;
  };

  useEffect(() => {
    if (user?.appName && !selectedOption) {
      setSelectedOption(user.appName);

      setSections((prevSections) =>
        prevSections.map((section) => {
          if (section.id === "uygulama-seçimi") {
            return { ...section, isExpanded: false };
          }
          if (section.id === "paket-seçimi") {
            return { ...section, isExpanded: true };
          }
          return section;
        }),
      );
    }
  }, [user?.appName, selectedOption]);

  const toggleSection = (sectionId: string): void => {
    if (sectionId === "paket-seçimi" && !selectedOption) {
      toast.warning("Önce uygulama seçimi yapmalısınız.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    if (
      sectionId === "ödeme-yöntemi" &&
      (!selectedOption || !selectedPackage)
    ) {
      if (!selectedOption) {
        toast.warning("Önce uygulama seçimi yapmalısınız.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (!selectedPackage) {
        toast.warning("Önce paket seçimi yapmalısınız.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      return;
    }

    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? { ...section, isExpanded: !section.isExpanded }
          : section,
      ),
    );
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>): void => {
    if (ref.current) {
      const headerHeight = 100;
      const elementPosition = ref.current.offsetTop - headerHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  const handleOptionSelect = (optionId: string): void => {
    setSelectedOption(optionId);
    setSelectedPackage(null);

    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === "uygulama-seçimi") {
          return { ...section, isExpanded: false };
        }
        if (section.id === "paket-seçimi") {
          return { ...section, isExpanded: true };
        }
        return section;
      }),
    );

    setTimeout(() => {
      scrollToSection(packageSelectionRef);
    }, 450);
  };

  const handlePackageSelect = (packageId: string): void => {
    setSelectedPackage(packageId);

    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === "paket-seçimi") {
          return { ...section, isExpanded: false };
        }
        if (section.id === "ödeme-yöntemi") {
          return { ...section, isExpanded: true };
        }
        return section;
      }),
    );

    setTimeout(() => {
      scrollToSection(paymentMethodRef);
    }, 450);
  };

  const handlePaymentSuccess = (): void => {
    setSelectedOption("");
    setSelectedPackage(null);

    if (
      paymentMethodChildRef.current &&
      paymentMethodChildRef.current.resetForm
    ) {
      paymentMethodChildRef.current.resetForm();
    }

    setSections([
      { id: "uygulama-seçimi", title: "Uygulama Seçimi", isExpanded: false },
      { id: "paket-seçimi", title: "Paket Seçimi", isExpanded: false },
      { id: "ödeme-yöntemi", title: "Ödeme Yöntemi", isExpanded: false },
    ]);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getCurrentApplication = (): ApplicationOption | undefined => {
    return applicationOptions.find((app) => app.id === selectedOption);
  };

  const getCurrentPackage = (): Package | undefined => {
    const filteredPackages = getFilteredPackages();
    return filteredPackages.find((pkg) => pkg.id === selectedPackage);
  };

  const getSection = (sectionId: string): Section | undefined => {
    return sections.find((s) => s.id === sectionId);
  };

  if (loading) {
    return <LoadingPreloader />;
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <img src={Logo} alt="Logo" />
          </div>
          <div className="user-section">
            <div className="user-info">
              <img src={People} alt="People" />
              <div className="user-details">
                <div className="user-name">{user?.studentName || ""}</div>
                <div className="user-class">{user?.gradeName || ""}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mobile-header">
          <div className="logo-section">
            <img src={Logo} alt="Logo" />
          </div>
          <div className="user-section">
            <div className="user-info">
              <img src={People} alt="People" />
              <div className="user-details">
                <div className="user-name">{user?.studentName || ""}</div>
                <div className="user-class">{user?.gradeName || ""}</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="section-card">
          <div
            className="section-header"
            onClick={() => toggleSection("uygulama-seçimi")}
          >
            <div className="section-title-group">
              <div
                className={classNames("step-indicator", {
                  completed: selectedOption !== "",
                  active: selectedOption === "",
                })}
              ></div>
              <h2 className="section-title">Uygulama Seçimi</h2>
            </div>
            <div className="selected-app-display-wrapper">
              {getCurrentApplication()?.title && (
                <div className="selected-app-display">
                  <span className="selected-app-text">
                    {getCurrentApplication()?.title}
                  </span>
                </div>
              )}
              <ChevronDown
                className={classNames("chevron", {
                  expanded: getSection("uygulama-seçimi")?.isExpanded,
                })}
              />
            </div>
          </div>

          <div
            className={classNames("accordion-content", {
              expanded: getSection("uygulama-seçimi")?.isExpanded,
            })}
          >
            <ApplicationSelection
              selectedOption={selectedOption}
              onOptionSelect={handleOptionSelect}
              apiWithSelectedApp={user?.appName ?? ""}
              setSelectedOption={setSelectedOption}
            />
          </div>
        </div>

        <div className="section-card" ref={packageSelectionRef}>
          <div
            className="section-header"
            onClick={() => toggleSection("paket-seçimi")}
          >
            <div className="section-title-group">
              <div
                className={classNames("step-indicator", {
                  completed: selectedPackage !== null,
                  active: selectedPackage === null,
                })}
              ></div>
              <h2 className="section-title">Paket Seçimi</h2>
            </div>
            <div className="selected-app-display-wrapper">
              {getCurrentPackage() && (
                <div className="selected-app-display">
                  <span className="selected-app-text">
                    {getCurrentPackage()?.description} -{" "}
                    {getCurrentPackage()?.price}₺
                  </span>
                </div>
              )}
              <ChevronDown
                className={classNames("chevron", {
                  expanded: getSection("paket-seçimi")?.isExpanded,
                })}
              />
            </div>
          </div>

          <div
            className={classNames("accordion-content", {
              expanded: getSection("paket-seçimi")?.isExpanded,
            })}
          >
            <PackageSelection
              selectedPackage={selectedPackage}
              selectedOption={selectedOption}
              onPackageSelect={handlePackageSelect}
              filteredPackages={getFilteredPackages()}
            />
          </div>
        </div>

        <div className="section-card" ref={paymentMethodRef}>
          <div
            className="section-header"
            onClick={() => toggleSection("ödeme-yöntemi")}
          >
            <div className="section-title-group">
              <div
                className={classNames("step-indicator", {
                  completed: false,
                  active: selectedOption !== "" && selectedPackage !== null,
                })}
              ></div>
              <h2 className="section-title">Ödeme Yöntemi</h2>
            </div>
            <ChevronDown
              className={classNames("chevron", {
                expanded: getSection("ödeme-yöntemi")?.isExpanded,
              })}
            />
          </div>

          <div
            className={classNames("accordion-content", {
              expanded: getSection("ödeme-yöntemi")?.isExpanded,
            })}
          >
            <PaymentMethod
              ref={paymentMethodChildRef}
              selectedApplication={selectedOption}
              selectedPackage={selectedPackage}
              uuid={uuid || ""}
              onPaymentSuccess={handlePaymentSuccess}
            />
          </div>
        </div>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default DopingHafizaApp;
